import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface CompanyOption { id: string; code: string; name: string }
export interface BranchOption { id: string; companyId: string; code: string; name: string }
interface Scope {
  companyId: string;
  branchId: string;
  companies: CompanyOption[];
  branches: BranchOption[];
  selectedCompany?: CompanyOption;
  selectedBranch?: BranchOption;
  setCompanyId(v: string): void;
  setBranchId(v: string): void;
  query: string;
}
const Ctx = createContext<Scope | null>(null);

export function ScopeProvider({ children }: { children: ReactNode }) {
  const companiesQ = useQuery({ queryKey: ['organization-companies'], queryFn: () => api<CompanyOption[]>('/organization/companies') });
  const branchesQ = useQuery({ queryKey: ['organization-branches'], queryFn: () => api<BranchOption[]>('/organization/branches') });
  const companies = companiesQ.data ?? [];
  const allBranches = branchesQ.data ?? [];
  const [companyId, setCompanyId] = useState('');
  const [branchId, setBranchId] = useState('');
  const branches = allBranches.filter(b => !companyId || b.companyId === companyId);

  useEffect(() => {
    if (!companies.some(c => c.id === companyId)) setCompanyId(companies[0]?.id ?? '');
  }, [companies, companyId]);
  useEffect(() => {
    if (!branches.some(b => b.id === branchId)) setBranchId(branches[0]?.id ?? '');
  }, [branches, branchId]);

  const value = useMemo<Scope>(() => ({
    companyId,
    branchId,
    companies,
    branches,
    selectedCompany: companies.find(c => c.id === companyId),
    selectedBranch: branches.find(b => b.id === branchId),
    setCompanyId,
    setBranchId,
    query: new URLSearchParams(Object.fromEntries(Object.entries({ companyId, branchId }).filter(([, v]) => v))).toString(),
  }), [companyId, branchId, companies, branches]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScope() { const value = useContext(Ctx); if (!value) throw new Error('ScopeProvider missing'); return value; }
